import {useState} from 'react'
import Modal from '@/customComponents/uiComponents/modal/Modal.jsx'
import ModalHeader from '@/customComponents/uiComponents/modal/ModalHeader.jsx'
import {RadioButton} from '@/customComponents/uiComponents/RadioButton.jsx'
import {BOOKING_CONSTRAINTS} from '@/constants/booking'

const MAX_LENGTH = BOOKING_CONSTRAINTS.SPECIAL_REQUEST_MAX_LENGTH

export default function SpecialRequestPopup({isOpen, onClose, specialRequest, onSave, notesPolicy = {canEdit: true, blockedReason: null}, petPolicy = {canEdit: true, blockedReason: null}, quietRidePolicy = {canEdit: true, blockedReason: null}}) {
    /**
     * ⚠️ THE EXISTING NOTE IS TRIMMED ON OPEN, and this popup is the reason that matters
     * more here than in the booking flow: the trip it edits was created BEFORE the cap
     * was enforced anywhere, so its stored `special_request` can already be over 500. A
     * passenger opening this to change their pickup time — never touching the note —
     * would otherwise submit that untouched over-length value and be rejected for a field
     * they did not edit.
     */
    const [draft, setDraft] = useState(() => ({
        ...specialRequest,
        notes: String(specialRequest?.notes || '').slice(0, MAX_LENGTH),
    }))

    const remaining = MAX_LENGTH - (draft.notes?.length || 0)

    const handleClose = () => {
        onClose?.()
    }

    const handleDone = () => {
        onSave?.({...draft})
    }

    const handleCancel = () => {
        const cleared = {
            notes: notesPolicy.canEdit ? '' : draft.notes,
            caringPet: petPolicy.canEdit ? false : draft.caringPet,
            quietRide: quietRidePolicy.canEdit ? false : draft.quietRide,
        }
        setDraft(cleared)
        onSave?.(cleared)
    }

    return (
        <Modal isOpen={isOpen} onClose={handleClose} className="md:max-w-137.5"
               footer={
                   <>
                       <button type="button" onClick={handleDone}
                               className="cursor-pointer w-full py-[15px] rounded-full bg-(--text-primary) text-white font-bold text-base flex items-center justify-center min-h-[55px] border-0 mb-3">Done</button>
                       <button type="button" onClick={handleCancel}
                               className="cursor-pointer w-full py-[15px] bg-transparent border-0 text-base text-(--text-primary) text-center">Cancel</button>
                   </>
               }>
            <ModalHeader title="Special Request" onClose={handleClose}/>
            <p className="text-(--text-secondary) text-sm mb-4">Let driver know more about your requests</p>
            <div className="flex gap-4 mb-6">
                <div className="relative group">
                    <label className={`flex items-center gap-2 text-sm ${petPolicy.canEdit ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}>
                        <RadioButton type="checkbox" disabled={!petPolicy.canEdit} checked={draft.caringPet} onChange={e => setDraft(p => ({...p, caringPet: e.target.checked}))}/>
                        Caring pet?
                    </label>
                    {!petPolicy.canEdit && petPolicy.blockedReason && (
                        <div className="hidden group-hover:block absolute top-full left-0 mt-3 bg-[#3c393c] text-white rounded-xl p-3 text-center text-sm z-10 w-max max-w-[280px] after:content-[''] after:absolute after:w-0 after:h-0 after:border-l-[9px] after:border-l-transparent after:border-r-24 after:border-r-transparent after:border-b-20 after:border-b-[#3c393c] after:-top-4.75 after:left-2">
                            <p className="mb-0!">{petPolicy.blockedReason}</p>
                        </div>
                    )}
                </div>
                <div className="relative group">
                    <label className={`flex items-center gap-2 text-sm ${quietRidePolicy.canEdit ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}>
                        <RadioButton type="checkbox" disabled={!quietRidePolicy.canEdit} checked={draft.quietRide} onChange={e => setDraft(p => ({...p, quietRide: e.target.checked}))}/>
                        Quiet Ride
                    </label>
                    {!quietRidePolicy.canEdit && quietRidePolicy.blockedReason && (
                        <div className="hidden group-hover:block absolute top-full left-0 mt-3 bg-[#3c393c] text-white rounded-xl p-3 text-center text-sm z-10 w-max max-w-[280px] after:content-[''] after:absolute after:w-0 after:h-0 after:border-l-[9px] after:border-l-transparent after:border-r-24 after:border-r-transparent after:border-b-20 after:border-b-[#3c393c] after:-top-4.75 after:left-2">
                            <p className="mb-0!">{quietRidePolicy.blockedReason}</p>
                        </div>
                    )}
                </div>
            </div>
            <h6 className="font-semibold mb-2!">Enter any instruction</h6>
            <div className="relative group">
                {/* Sliced as well as `maxLength`-capped: `maxLength` does not apply to a
                    paste in every browser, and a paste is how an over-long instruction
                    arrives. */}
                <textarea disabled={!notesPolicy.canEdit} value={draft.notes}
                          onChange={e => setDraft(p => ({...p, notes: e.target.value.slice(0, MAX_LENGTH)}))}
                          maxLength={MAX_LENGTH} rows={4}
                          className="w-full! border border-(--border-color) rounded-(--radius-2xl) p-5 text-base outline-none resize-none mb-2 disabled:opacity-50 disabled:cursor-not-allowed"/>

                {/* Hidden when the field is locked by policy — a count of characters left
                    in a box that cannot be typed in is noise. */}
                {notesPolicy.canEdit && (
                    <p className={`text-right text-xs mb-5! ${remaining <= 50 ? 'text-(--text-red) font-semibold' : 'text-(--text-secondary)'}`}>
                        {remaining} characters left
                    </p>
                )}
                {!notesPolicy.canEdit && notesPolicy.blockedReason && (
                    <div className="hidden group-hover:block absolute top-full left-0 mt-3 bg-[#3c393c] text-white rounded-xl p-3 text-center text-sm z-10 w-max max-w-[280px] after:content-[''] after:absolute after:w-0 after:h-0 after:border-l-[9px] after:border-l-transparent after:border-r-24 after:border-r-transparent after:border-b-20 after:border-b-[#3c393c] after:-top-4.75 after:left-2">
                        <p className="mb-0!">{notesPolicy.blockedReason}</p>
                    </div>
                )}
            </div>
        </Modal>
    )
}
